'use client';

import { courseScheduleHref } from '@/app/lib/scheduleCourseHandoff';
import type { MouseEvent } from 'react';
import Link from 'next/link';
import { CampusChips } from './DiscoveryLabels';
import { StarIcon } from '@heroicons/react/24/outline';
import { useUiPreferences } from '@/app/Components/UiPreferencesProvider';
import { getDepartmentAcademicSwatch } from '@/app/lib/academicPalette';
import { formatBaseIdDisplay, formatDepartmentDisplay, isGradCourse } from '@/app/lib/utils';
import type { ExploreCourseData } from '@/app/types/interfaces';
import { Button, buttonClassName } from '@/app/Components/Button';
import { OperationalStatus, Surface } from '@/app/Components/OperationalPage';
import { getCampusColor } from '@/app/types/colors';
import { createSchedulePatterns, getSingleCampusAccent, usefulInstructorNames } from './coursePresentation';
import { hasRecitationMeeting } from '@/app/lib/meetingTypes';

interface CourseCardProps {
  course: ExploreCourseData;
  semester?: string;
  completed?: boolean;
  onClick: (course: ExploreCourseData) => void;
  onContextMenu: (event: MouseEvent, course: ExploreCourseData) => void;
  viewMode?: 'grid' | 'list' | 'cards' | 'dense';
  showDepartmentColor?: boolean;
  showActions?: boolean;
}

export default function ExploreCourseCard({
  course,
  semester,
  completed = false,
  onClick,
  onContextMenu,
  viewMode = 'grid',
  showDepartmentColor = false,
  showActions = true,
}: CourseCardProps) {
  const { academicPalettePreferences, academicPalettePresets } = useUiPreferences();
  const isList = viewMode === 'list' || viewMode === 'dense';
  const departmentSwatch = getDepartmentAcademicSwatch(
    course.department,
    academicPalettePreferences.coursePaletteId,
    academicPalettePresets
  );
  const instructors = usefulInstructorNames(course);
  const totalSectionsCount = course.totalSectionsCount ?? course.sections.length;
  const fullTitle = course.fullTitle?.trim();
  const courseName = course.courseName?.trim();
  const usefulTitle = fullTitle || (courseName && !/^unknown\s*course$/i.test(courseName) ? courseName : '');
  const title = usefulTitle || 'Course title unavailable';
  const actionIdentity = usefulTitle || formatBaseIdDisplay(course.baseId) || title;
  const scheduleLabel = totalSectionsCount > 0 ? 'Schedule' : 'Search sections';
  const hasFullSectionSet = course.sections.length >= totalSectionsCount;
  const schedulePatterns = course.schedulePatterns?.length
    ? course.schedulePatterns
    : hasFullSectionSet
      ? createSchedulePatterns(course.sections)
      : [];
  const scheduleCampuses = [...new Set((course.campuses?.length ? course.campuses : schedulePatterns.map((pattern) => pattern.campus)).filter((campus) => campus && !/^unknown$/i.test(campus)))];
  const hasFacts = Boolean(schedulePatterns.length || course.schedulePatternCount || instructors.length || totalSectionsCount > 1);
  const hasRecitation = course.meetingTypes?.some((mode) => /^recit(ation)?$/i.test(mode))
    ?? schedulePatterns.some((pattern) => hasRecitationMeeting(pattern.meetingTimes));
  const campusAccent = getSingleCampusAccent(course);

  if (isList) return <article data-course-card-view="list" data-campus-accent={campusAccent ? "single" : "unassigned"} className="group relative border-b border-line py-3 pl-3 last:border-b-0" onContextMenu={event => onContextMenu(event, course)}>
    {campusAccent ? <span data-campus-accent-bar aria-hidden="true" className={`absolute inset-y-3 left-0 w-1 ${campusAccent}`} /> : null}
    <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 sm:grid-cols-[6rem_minmax(0,1fr)_auto]">
      <span className="hidden font-mono text-xs text-content-muted sm:block">{formatBaseIdDisplay(course.baseId)}</span>
      <button type="button" onClick={() => onClick(course)} className="min-h-12 min-w-0 text-left text-sm font-semibold text-content hover:text-focus focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"><span className="mb-1 block font-mono text-xs font-normal text-content-muted sm:hidden">{formatBaseIdDisplay(course.baseId)}</span><h2 className="inline text-sm font-semibold">{title}</h2></button>
      <div className="flex shrink-0 items-center gap-1"><OperationalStatus tone={course.openSectionsCount ? 'positive' : 'neutral'}>{totalSectionsCount > 0 ? `${course.openSectionsCount}/${totalSectionsCount} open` : 'No sections in this snapshot'}</OperationalStatus>{showActions ? <Button variant="subtle" aria-label={`Actions for ${actionIdentity}`} onClick={event=>onContextMenu(event, course)}>•••</Button> : null}</div>
      <div className="col-span-full flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-content-muted sm:col-start-2 sm:col-end-3"><span>{course.credits} credits</span><CampusChips campuses={scheduleCampuses} />{course.averageRating > 0 ? <span className="inline-flex items-center gap-1" title={`${course.totalRatings} matched reviews; difficulty ${course.averageDifficulty.toFixed(1)}/5`}><StarIcon aria-hidden="true" className="h-4 w-4" />{course.averageRating.toFixed(1)}/5</span> : null}{instructors.length ? <span className="max-w-64 truncate" title={instructors.join(', ')}>{instructors.slice(0,2).join(', ')}{instructors.length>2?` +${instructors.length-2}`:''}</span> : null}{completed ? <span>Completed</span> : null}</div>
      <Link href={courseScheduleHref(course.baseId, semester)} className="col-start-2 inline-flex min-h-12 items-center justify-end text-xs font-semibold text-focus sm:col-start-3">{scheduleLabel}<span className="sr-only"> {actionIdentity}</span></Link>
    </div>
  </article>;

  const identity = (
    <button
      onClick={() => onClick(course)}
      className="min-w-0 text-left outline-none focus-visible:rounded-control focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-xs font-semibold text-focus">
          {formatBaseIdDisplay(course.baseId)}
        </span>
        {isGradCourse(course.baseId, course.class_number) && <OperationalStatus tone="info">GRAD</OperationalStatus>}
        {completed && <OperationalStatus tone="positive">COMPLETED</OperationalStatus>}
        <OperationalStatus tone={totalSectionsCount > 0 && course.hasOpenSections ? 'positive' : 'neutral'}>
          {totalSectionsCount > 0 ? `${course.openSectionsCount}/${totalSectionsCount} open` : 'No sections in this snapshot'}
        </OperationalStatus>
        {hasRecitation && <OperationalStatus tone="info">Recitation</OperationalStatus>}
      </div>
      <h2 className={`${isList ? 'mt-1 text-sm' : 'mt-1 text-base'} font-semibold leading-snug group-hover:text-focus`}>
        {title}
      </h2>
      <p className="mt-1 flex flex-wrap items-center gap-x-1.5 text-xs text-content-muted">
        <span
          className={showDepartmentColor ? 'rounded-full border px-2 py-0.5 font-medium' : undefined}
          style={showDepartmentColor ? {
            backgroundColor: departmentSwatch.soft,
            borderColor: departmentSwatch.border,
            color: 'var(--ss-text)',
          } : undefined}
        >
          {formatDepartmentDisplay(course.department)}
        </span>
        <span aria-hidden="true">·</span>
        <span>{course.credits} credits</span>
        {course.coreRequirements.length > 0 && <><span aria-hidden="true">·</span><span>Core: {course.coreRequirements.join(', ')}</span></>}
      </p>
    </button>
  );

  const facts = (hasFacts || course.averageRating > 0) ? (
    <button
      onClick={() => onClick(course)}
      className={`${isList ? 'grid content-center gap-1.5 lg:border-l lg:border-line lg:pl-4' : 'grid grid-cols-1 gap-2 border-t border-line pt-3 sm:grid-cols-2'} w-full text-left text-xs text-content-muted`}
    >
      {(schedulePatterns.length || course.schedulePatternCount || totalSectionsCount > 1) ? <span className="col-span-full flex min-w-0 flex-wrap gap-1.5" aria-label={scheduleCampuses.length ? `Campuses: ${scheduleCampuses.join(', ')}` : 'Campus details unavailable'}>{scheduleCampuses.length ? scheduleCampuses.map((campus) => <span key={campus} className={`rounded-control px-2 py-0.5 text-[11px] font-semibold ${getCampusColor(campus, 'badge')}`}>{campus}</span>) : <span className="text-content-muted">Compare section locations in course details</span>}</span> : null}
      {instructors.length > 0 ? <span className="col-span-full flex flex-wrap gap-1.5" aria-label={`All instructors: ${instructors.join(', ')}`}>{instructors.map((name) => <span key={name} className="rounded-control border border-line bg-surface-subtle px-2 py-0.5 text-content">{name}</span>)}</span> : <span className="col-span-full">Instructors not assigned</span>}
      {course.averageRating > 0 ? <span className="col-span-full inline-flex min-w-0 items-center gap-1"><StarIcon className="h-4 w-4 shrink-0"/><span>Professor average {course.averageRating.toFixed(1)}/5 across {course.professorRatings?.length ?? 0} rated instructor{course.professorRatings?.length === 1 ? '' : 's'} · Difficulty {course.averageDifficulty.toFixed(1)}{course.averageWouldTakeAgain >= 0 ? ` · ${course.averageWouldTakeAgain.toFixed(0)}% would take again` : ''}</span></span> : <span className="col-span-full inline-flex items-center gap-1"><StarIcon className="h-4 w-4"/>No matched professor ratings available</span>}
    </button>
  ) : null;

  return (
    <Surface
      data-course-card-view={isList ? 'list' : 'grid'}
      data-department-color={showDepartmentColor ? 'enabled' : 'disabled'}
      data-campus-accent={campusAccent ? 'single' : 'unassigned'}
      className={`group relative overflow-hidden transition ${isList ? 'p-3.5 lg:grid lg:grid-cols-[minmax(0,1.35fr)_minmax(260px,0.95fr)_auto] lg:items-center lg:gap-4' : 'p-4'}`}
    >
      {/* exact-use contract: academic palette color is data visualization only; department text remains adjacent. */}
      {campusAccent && <span data-campus-accent-bar aria-hidden="true" className={`absolute inset-y-0 left-0 w-1 ${campusAccent}`} />}
      {showDepartmentColor && <span data-department-accent aria-hidden="true" className="absolute inset-y-0 left-1 w-1" style={{ backgroundColor: departmentSwatch.solid }} />}
      <div className={isList ? 'min-w-0 pl-1' : 'flex min-w-0 flex-col gap-2 pl-1 sm:flex-row'}>
        <div className="min-w-0 flex-1">{identity}</div>
        {!isList && <div className="flex shrink-0 items-start justify-end gap-1">
          <Link href={courseScheduleHref(course.baseId, semester)} className={buttonClassName({ variant: 'link' })}>{scheduleLabel}</Link>
          {showActions && <Button variant="subtle" onClick={(event) => onContextMenu(event, course)} aria-label={`Actions for ${actionIdentity}`}>•••</Button>}
        </div>}
      </div>
      <div className={isList ? 'mt-3 lg:mt-0' : 'mt-3'}>{facts}</div>
      {isList && <div className="mt-2 flex items-center justify-end gap-1 lg:mt-0">
        <Link href={courseScheduleHref(course.baseId, semester)} className={buttonClassName({ variant: 'link' })}>{scheduleLabel}</Link>
        {showActions && <Button variant="subtle" onClick={(event) => onContextMenu(event, course)} aria-label={`Actions for ${actionIdentity}`}>•••</Button>}
      </div>}
    </Surface>
  );
}
